import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { ModalNextProvider } from '@jutro/components';
import { useValidation } from 'gw-portals-validation-react';
import { useDependencies } from 'gw-portals-dependency-react';
import moment from 'moment';
import { DateUtil } from 'gw-portals-util-js';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { TranslatorContext } from '@jutro/locale';
import { error } from '@jutro/logger';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { Contact, Claim } from 'gw-capability-fnol-common-react';
import { ViewModelForm, withViewModelService, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { messages as commonMessages } from 'gw-platform-translations';
import metadata from './WhatPage.metadata.json5';
import messages from '../../FNOLWC7.messages';
import styles from './WhatPage.module.scss';

function FNOLWC7WhatPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        wizardData: claimVM,
        updateWizardData,
        authHeader,
        history: {
            location: { state }
        },
        loggedUserContact
    } = props;
    const translator = useContext(TranslatorContext);
    const { FNOLService } = useDependencies('FNOLService');
    const viewModelService = useContext(ViewModelServiceContext);
    const [isLoading, setIsLoading] = useState(false);
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLWC7WhatPage');

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'com.tdic.edge.capabilities.claim.fnol.dto.FnolDTO_Ext',
            {
                InjuredData: true,
                TypeOfIncidentReporter: true
            }
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const accountDetails = useCallback(async () => {
        const newClaimVM = initializeVM();
        const reporter = {
            taxID: null,
            lastName: _.get(loggedUserContact, 'accountContact.lastName', ''),
            firstName: _.get(loggedUserContact, 'accountContact.firstName', ''),
            adanumber: _.get(loggedUserContact, 'accountContact.adaNumber_Ext', '')
        };
        _.set(newClaimVM, 'reporter', reporter);
        updateWizardData(newClaimVM);
        const lobExtension = newClaimVM.lobs.wc7workersComp_Ext;
        const { injuryIncident } = lobExtension.value;

        if (!injuryIncident.injured) {
            injuryIncident.injured = new Contact({
                displayName: 'New person',
                gender: null,
                dateOfBirth: null
            });
        }

        if (!injuryIncident.injured.primaryAddress) {
            injuryIncident.injured.initializePrimaryAddress();
        }
    }, [initializeVM, loggedUserContact, updateWizardData]);

    useEffect(
        () => {
            accountDetails();
        },
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const getNameValue = useCallback(() => {
        return _.get(loggedUserContact, 'accountContact.displayName', '');
    }, [loggedUserContact]);

    const getAvailablevalues = useCallback(
        () => {
            const dropdownData = _.get(claimVM, 'lobs.wc7workersComp_Ext.employementData.employementStatus.aspects.availableValues', []);
            const dropDownContacts = dropdownData.map((data) => {
                return {
                    code: data.code,
                    name: translator({
                        id: data.name,
                        defaultMessage: data.name
                    })
                };
            });
            return dropDownContacts;
            // Call once when page is mount
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []
    );

    const writeValue = useCallback(
        (value, path) => {
            const newClaimVM = _.clone(claimVM);
            _.set(newClaimVM.value, path, value);
            if (path === 'lobs.wc7workersComp_Ext.employementData.grossWeeklyWage' && _.isNil(value.amount)) {
                _.set(newClaimVM.value, path, null);
            }
            updateWizardData(newClaimVM);
        },
        [claimVM, updateWizardData]
    );

    const getClaimType = useCallback(() => {
        _.set(claimVM.value, 'typeOfClaim_Ext', 'workerscompensation');
    }, [claimVM]);

    const getValues = useCallback(() => {
        const claimValue = {
            lossDate: _.get(claimVM.value, 'lossDate'),
            lossType: _.get(claimVM.value, 'lossType'),
            lossCause: _.get(claimVM.value, 'lossCause'),
            description_Ext: _.get(claimVM.value, 'description_Ext'),
            reporter: _.get(claimVM.value, 'reporter'),
            typeOfClaim_Ext: _.get(claimVM.value, 'typeOfClaim_Ext'),
            policy: _.omit(claimVM.value.policy, ['offeringCode']),
            lobs:
            {
                WC7WorkersComp_Ext: _.get(claimVM.value, 'lobs.wc7workersComp_Ext')
            }
        };
        return claimValue;
    },
    [claimVM]);

    const checkDateWithinRange = useCallback(() => {
        const dateOfNotice = _.get(claimVM.value, 'lobs.wc7workersComp_Ext.dateReportedToEmployer');
        if (!_.isEmpty(dateOfNotice)) {
            const lossDate = _.get(claimVM.value, 'lossDate');
            const reportedDate = moment().format();
            const dateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                lossDate);
            const changeDateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                reportedDate);
            return !((dateStatus === 1 || dateStatus === 0)
            && (changeDateStatus === -1 || changeDateStatus === 0));
        }
        return false;
    }, [claimVM]);

    const onNext = useCallback(() => {
        const claimValue = getValues();
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (!isClaimNumberAvailable) {
            setIsLoading(true);
            return FNOLService.getFNOLDetails(policyNumber, claimValue, authHeader)
                .then((response) => {
                    const reportedDetails = _.get(claimVM.value, 'reporter');
                    claimVM.value = new Claim(response);
                    _.set(claimVM.value, 'reporter', reportedDetails);
                    return claimVM;
                })
                .catch((err) => {
                    error(`WhatPage: getFNOLDetails - Unable to get FNOL details ${JSON.stringify(err)}`);
                    ModalNextProvider.showAlert({
                        title: messages.wcCreateDraftErrorTitle,
                        message: messages.wcCreateDraftErrorMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                    return false;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        return claimVM;
    }, [FNOLService, authHeader, claimVM, getValues]);

    const resolvers = {
        resolveClassNameMap: styles,
    };

    const overrides = {
        '@field': {
            showOptional: true,
            labelPosition: breakpoint === 'tablet' ? 'top' : 'left'
        },
        whatPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(messages.wcCreateDraftClaim)
        },
        whatPageContainer: {
            visible: !isLoading
        },
        employmentStatus: {
            availableValues: getAvailablevalues()
        },
        whatName: {
            required: true,
            value: getNameValue()
        },
        whatInjuryCauseSource: {
            value: getClaimType()
        },
        whatDetailCauseOfIncident: {
            required: true,
        },
        dateEmployerNotified: {
            showErrors: checkDateWithinRange(),
            validationMessages: checkDateWithinRange()
                ? [translator(messages.invalidDateMessage)] : []
        }
    };

    const { claimStatus } = state || {};

    const validateClaimStatus = useCallback(() => {
        return (
            !_.isNil(claimStatus)
            && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' })
        );
    }, [claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [registerInitialComponentValidation, validateClaimStatus]);

    return (
        <WizardPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            onNext={onNext}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLWC7WhatPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(withViewModelService(
    withApplicationPropertyContext(FNOLWC7WhatPage)
)));
